import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { MatButtonModule } from '@angular/material/button';
import { Subscription } from 'rxjs';
import packageJson from '../../../../package.json';
import { environment } from '../../../environments/environment';
import { MenuItem } from './models/MenuItem';
import { MenuItems } from './models/MenuItems';
import { ModuleAccessService, ALL_MODULES } from '../../infrastructure/auth/module-access.service';
import { OperationAccessService } from '../../infrastructure/auth/operation-access.service';
import { can } from '../../infrastructure/auth/permission-keys';
import { PERMISSION_MODULE_IDS, moduleIdFromLink, requiresPermission } from '../../feature/permissions/models/Module';
import { SCREENING_REPORT_LINK, canViewScreeningReport } from '../../feature/screening-report/screening-report-access';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatTooltipModule,
    RouterLink,
    MatListModule,
    MatButtonModule
  ]
})
export class SidebarComponent implements OnInit, OnDestroy {

  @Input() isExpanded: boolean | undefined;
  @Output() toggleMenu = new EventEmitter();

  @ViewChild('mainBody') mainBody: ElementRef | undefined;

  menuItems: MenuItem[] = this.getDashboardOnlyMenuItems();
  navSubscription: Subscription | undefined;
  appVersion: string = packageJson.version;
  showVersion: boolean = false;

  constructor(
    public router: Router,
    private msal: MsalService,
    private moduleAccessService: ModuleAccessService,
    private operationAccessService: OperationAccessService
  ) { }

  ngOnInit(): void {
    this.showVersion = !environment.production;
    this.loadMenuItems();
  }

  async loadMenuItems(): Promise<void> {
    const allowed = await this.moduleAccessService.getAllowedModuleIds();
    const isSuperAdmin = allowed === ALL_MODULES;

    // Gated modules need both the module and its "view" permission, matching moduleAccessGuard.
    const viewable = new Set<string>();
    for (const moduleId of PERMISSION_MODULE_IDS) {
      if (await this.operationAccessService.hasPermission(can(moduleId, 'view'))) {
        viewable.add(moduleId);
      }
    }

    this.menuItems = MenuItems.filter(item => {
      if (item.link === SCREENING_REPORT_LINK) {
        return canViewScreeningReport(this.msal);
      }

      if (item.link === '/permissions') {
        return isSuperAdmin;
      }

      if (!item.link || !requiresPermission(moduleIdFromLink(item.link))) {
        return true;
      }

      const moduleId = moduleIdFromLink(item.link);
      return isSuperAdmin || (allowed.includes(moduleId) && viewable.has(moduleId));
    });
  }

  private getDashboardOnlyMenuItems(): MenuItem[] {
    return MenuItems.filter(item => item.link === '/dashboard');
  }

  ngOnDestroy(): void {
    if (this.navSubscription) {
      this.navSubscription.unsubscribe();
    }
  }
}
